import { useState } from 'react';
import {
  Alert as AntAlert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  ALERT_SEVERITIES,
  ALERT_SOURCES,
  RULE_ACTIONS,
  type AlertRuleRequest,
  type AlertRuleView,
  type RuleAction,
} from '@/api/alerts';

const { Text } = Typography;

interface FormShape {
  name: string;
  priority: number;
  matchType?: string;
  matchSeverity?: string;
  matchSource?: string;
  matchDetail: { key: string; value: string }[];
  action: RuleAction;
  flowId?: string;
  flowVersion?: number;
  nodeId?: string;
  decision?: string;
}

export interface RuleFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Resolves on a 2xx; rejections stay in the modal as the API's own message. */
  onCreate: (request: AlertRuleRequest) => Promise<void>;
  /**
   * "Duplicate as replacement" (OPSFL-FIX-1): prefill every field from this rule. The modal
   * still only ever CREATES — the honest path around the deliberate no-edit-in-place posture.
   * Render with a changing `key` so a different source remounts the form cleanly.
   */
  prefillFrom?: AlertRuleView | null;
}

/** The prefilled shape for a replacement — name suffixed so the unique-name 409 can't bite. */
function initialShape(prefillFrom: AlertRuleView | null | undefined): FormShape {
  if (!prefillFrom) {
    return { name: '', priority: 100, action: 'START_FLOW', matchDetail: [] };
  }
  const action = (RULE_ACTIONS as readonly string[]).includes(prefillFrom.action)
    ? (prefillFrom.action as RuleAction)
    : 'START_FLOW';
  return {
    name: `${prefillFrom.name} (replacement)`,
    priority: prefillFrom.priority,
    matchType: prefillFrom.matchType ?? undefined,
    matchSeverity: prefillFrom.matchSeverity ?? undefined,
    matchSource: prefillFrom.matchSource ?? undefined,
    matchDetail: Object.entries(prefillFrom.matchDetail ?? {}).map(([key, value]) => ({
      key,
      value: String(value),
    })),
    action,
    flowId: prefillFrom.flowId ?? undefined,
    flowVersion: prefillFrom.flowVersion ?? undefined,
    nodeId: prefillFrom.nodeId ?? undefined,
    decision: prefillFrom.decision ?? undefined,
  };
}

/**
 * New routing rule (OPSFL-ALUI-1). The vocabulary is deliberately the API's, nothing more:
 * flat field=value equality conditions (§7.4 — no operators, no nesting; the server 400s
 * anything else) and one of three actions. First matching rule by priority wins. There is no
 * edit-in-place on the API — DELIBERATE (explainability; owner-confirmed 2026-08-15): a
 * correction is a NEW rule plus disabling the old one, and "Duplicate as replacement"
 * ({@link RuleFormModalProps.prefillFrom}) makes that honest path one prefilled step — two
 * explicit acts, both recorded, so the "why did this fire in March" trail stays intact.
 */
export function RuleFormModal({ open, onClose, onCreate, prefillFrom }: RuleFormModalProps) {
  const [form] = Form.useForm<FormShape>();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const initial = initialShape(prefillFrom);
  const action = Form.useWatch('action', form) ?? initial.action;

  const submit = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    setSubmitError(null);
    const matchDetail: Record<string, string> = {};
    for (const pair of values.matchDetail ?? []) {
      if (pair?.key?.trim()) matchDetail[pair.key.trim()] = pair.value ?? '';
    }
    try {
      await onCreate({
        name: values.name.trim(),
        priority: values.priority,
        matchType: values.matchType?.trim() || null,
        matchSeverity: values.matchSeverity || null,
        matchSource: values.matchSource || null,
        matchDetail: Object.keys(matchDetail).length ? matchDetail : null,
        action: values.action,
        flowId: values.action === 'START_FLOW' ? values.flowId?.trim() || null : null,
        flowVersion: values.action === 'START_FLOW' ? (values.flowVersion ?? null) : null,
        nodeId: values.action === 'SIGNAL_RUN' ? values.nodeId?.trim() || null : null,
        decision: values.action === 'SIGNAL_RUN' ? values.decision?.trim() || null : null,
      });
      form.resetFields();
      onClose();
    } catch (e) {
      // Shown verbatim in the modal — the API names exactly what it refused (action shape,
      // nested matchDetail, duplicate name, permission).
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={prefillFrom ? `Duplicate as replacement — ${prefillFrom.name}` : 'New routing rule'}
      onCancel={() => {
        setSubmitError(null);
        onClose();
      }}
      onOk={() => void submit()}
      okText={prefillFrom ? 'Create replacement' : 'Create rule'}
      confirmLoading={submitting}
      width={620}
      destroyOnClose
    >
      <Text type="secondary" style={{ fontSize: 12.5, display: 'block', marginBottom: 12 }}>
        Conditions are flat equality only — every listed condition must match exactly. The first
        enabled rule by priority (lowest number first) wins. A START_FLOW rule can only start a
        <b> published</b> flow version; if it cannot, the alert stays OPEN with the failure shown
        on the register.
      </Text>

      {prefillFrom && (
        <AntAlert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Rules are never edited in place"
          description={
            <span style={{ fontSize: 12 }}>
              Every field below is copied from{' '}
              <Text className="ol-mono" style={{ fontSize: 11 }}>
                {prefillFrom.name}
              </Text>
              . Adjust what needs correcting and create — then you&rsquo;ll be offered a one-click
              disable of the original. Two explicit acts, both recorded, so past routings stay
              explainable.
            </span>
          }
        />
      )}

      {submitError && (
        <AntAlert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="The API refused this rule"
          description={submitError}
        />
      )}

      <Form<FormShape> form={form} layout="vertical" initialValues={initial}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Form.Item
            name="name"
            label="Name"
            style={{ flex: 1 }}
            rules={[{ required: true, whitespace: true, message: 'A rule needs a name (unique per hub).' }]}
          >
            <Input placeholder="e.g. high-severity screening hits → EDD flow" maxLength={120} />
          </Form.Item>
          <Form.Item
            name="priority"
            label="Priority"
            tooltip="Lowest number wins. Ties broken by creation order."
            rules={[{ required: true, message: 'Priority is required.' }]}
          >
            <InputNumber min={0} max={100000} style={{ width: 110 }} />
          </Form.Item>
        </div>

        <Text strong style={{ fontSize: 12.5 }}>
          Match — all conditions must hold (leave blank to match any)
        </Text>
        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <Form.Item name="matchType" label="Type" style={{ flex: 1 }}>
            <Input placeholder="e.g. SLA_BREACH, SIGNAL_RECEIVED" maxLength={60} />
          </Form.Item>
          <Form.Item name="matchSeverity" label="Severity" style={{ width: 130 }}>
            <Select allowClear options={ALERT_SEVERITIES.map((s) => ({ value: s, label: s }))} />
          </Form.Item>
          <Form.Item name="matchSource" label="Source" style={{ width: 150 }}>
            <Select allowClear options={ALERT_SOURCES.map((s) => ({ value: s, label: s }))} />
          </Form.Item>
        </div>

        <Form.List name="matchDetail">
          {(fields, { add, remove }) => (
            <div style={{ marginBottom: 16 }}>
              {fields.map((field) => (
                <div key={field.key} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <Form.Item
                    name={[field.name, 'key']}
                    noStyle
                    rules={[{ required: true, message: 'Field name required.' }]}
                  >
                    <Input placeholder="detail field (e.g. list_id)" style={{ flex: 1 }} aria-label="Detail field name" />
                  </Form.Item>
                  <Form.Item name={[field.name, 'value']} noStyle>
                    <Input placeholder="required value" style={{ flex: 1 }} aria-label="Required value" />
                  </Form.Item>
                  <Button
                    icon={<DeleteOutlined />}
                    aria-label="Remove condition"
                    onClick={() => remove(field.name)}
                  />
                </div>
              ))}
              <Button size="small" icon={<PlusOutlined />} onClick={() => add({ key: '', value: '' })}>
                Add detail condition (field = value)
              </Button>
            </div>
          )}
        </Form.List>

        <Form.Item name="action" label="Action">
          <Segmented
            options={[
              { label: 'Start a flow', value: 'START_FLOW' },
              { label: 'Signal a running flow', value: 'SIGNAL_RUN' },
              { label: 'Suppress', value: 'SUPPRESS' },
            ]}
          />
        </Form.Item>

        {action === 'START_FLOW' && (
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item
              name="flowId"
              label="Flow id"
              style={{ flex: 1 }}
              rules={[{ required: true, whitespace: true, message: 'START_FLOW needs a flow id.' }]}
            >
              <Input placeholder="a PUBLISHED flow's id" />
            </Form.Item>
            <Form.Item name="flowVersion" label="Version" tooltip="Blank = current published version.">
              <InputNumber min={1} style={{ width: 110 }} />
            </Form.Item>
          </div>
        )}

        {action === 'SIGNAL_RUN' && (
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item
              name="nodeId"
              label="Node id"
              style={{ flex: 1 }}
              rules={[{ required: true, whitespace: true, message: 'SIGNAL_RUN needs a node id.' }]}
            >
              <Input placeholder="the waiting node in the alert's run" />
            </Form.Item>
            <Form.Item
              name="decision"
              label="Decision"
              style={{ flex: 1 }}
              rules={[{ required: true, whitespace: true, message: 'SIGNAL_RUN needs a decision.' }]}
            >
              <Input placeholder="decision token to deliver" maxLength={80} />
            </Form.Item>
          </div>
        )}

        {action === 'SUPPRESS' && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Suppress records the match and stops — the alert lands as SUPPRESSED, still on the
            register, never deleted.
          </Text>
        )}
      </Form>
    </Modal>
  );
}
